import { BookOpen, MessageCircleQuestion } from "lucide-react";
import { useContext, useEffect, useState } from "react";

import { QuestionMarkCircle } from "./icons";
import { Button } from "./ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { UserContext } from "@/contexts/UserContext";
import sendStandardEvent from "@/hooks/use-firebase-event";

type WizardStepProps = React.ComponentProps<typeof Card> & {
  title: string;
  subtitle?: string;
  description?: string;
  onPrevStep?: () => void;
  onNextStep?: () => void;
  onFinishStep?: () => void;
  enablePrevStep?: boolean;
  enableNextStep?: boolean;
  enableFinishStep?: boolean;
  className?: string;
};

export default function WizardStep({
  title,
  subtitle,
  description,
  onPrevStep,
  onNextStep,
  onFinishStep,
  enablePrevStep = true,
  enableNextStep = true,
  enableFinishStep = true,
  children,
  className,
}: WizardStepProps) {
  const { user } = useContext(UserContext);
  const [showHelpSection, setShowHelpSection] = useState(false);

  useEffect(() => {
    if (user) {
      $crisp.push(["set", "user:email", [user.email]]);
      $crisp.push(["set", "user:nickname", [user.displayName || user.email]]);
    }
  }, [user]);

  return (
    <Card
      className={`w-full lg:max-w-6xl mx-auto overflow-x-hidden ${className}`}
    >
      <CardHeader>
        {subtitle && (
          <h2 className="text-sm text-sidebar-primary">{subtitle}</h2>
        )}
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">{children}</CardContent>
      <CardFooter className="justify-between">
        <Button
          variant="secondary"
          onClick={() => {
            sendStandardEvent("Clicked support", "onboarding", user);
            setShowHelpSection(!showHelpSection);
          }}
        >
          <QuestionMarkCircle color="info" />I need help
        </Button>
        <CardAction className="grid grid-flow-col gap-2">
          {onPrevStep && (
            <Button
              disabled={!enablePrevStep}
              onClick={onPrevStep}
              variant="secondary"
            >
              Back
            </Button>
          )}
          {onNextStep && (
            <Button
              disabled={!enableNextStep}
              onClick={onNextStep}
              variant="default"
            >
              Next
            </Button>
          )}
          {onFinishStep && (
            <Button
              disabled={!enableFinishStep}
              onClick={onFinishStep}
              variant="default"
            >
              Finish
            </Button>
          )}
        </CardAction>
      </CardFooter>
      {showHelpSection && (
        <section className="flex flex-col gap-4 w-full p-6 border-t">
          <h3 className="text-lg font-semibold">Need Pomerium Support?</h3>
          <p>
            If you're having trouble with Pomerium, check out our documentation
            website for more details or get chat support from our team:
          </p>
          <div className="flex gap-4">
            <a href="https://pomerium.com/docs" target="_blank">
              <Button variant="secondary">
                <BookOpen color="#42a5f5" className="size-5" />
                Documentation
              </Button>
            </a>
            <Button
              variant="default"
              onClick={() => {
                $crisp.push(["do", "chat:show"]);
                $crisp.push(["do", "chat:open"]);
              }}
            >
              <MessageCircleQuestion />
              Chat Support
            </Button>
          </div>
        </section>
      )}
    </Card>
  );
}
